import { Button, Loading, useToggle } from '@commenda/kakashi';
import {
  CalendarAgendaView,
  navigateToEventDetail,
  type RbcEvent,
} from '@commenda/ui';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { SectionTitle } from './SectionTitle';
import { useUpcomingFilings } from './useUpcomingFilings';

export const UpcomingFilings = () => {
  const router = useRouter();

  const section = useToggle(true);

  const {
    eventsByDate,
    isLoading,
    calendarNotGenerated,
    today,
    windowEnd,
    windowDays,
  } = useUpcomingFilings();

  const hasFilings = eventsByDate.size > 0;

  const handleSelectEvent = useCallback(
    (event: RbcEvent) => {
      navigateToEventDetail(event.resource, router);
    },
    [router]
  );

  return (
    <section className="flex flex-col overflow-hidden rounded-xl border border-border-secondary bg-bg-primary shadow-[0px_4px_24px_0px_#0000000A]">
      <div
        className={`flex items-center justify-between py-lg pl-lg pr-2xl ${
          section.visible ? 'border-b border-border-secondary' : ''
        }`}
      >
        <SectionTitle
          title="Upcoming filings"
          isExpanded={section.visible}
          onToggle={section.toggle}
        />
        {!calendarNotGenerated && (
          <Button
            variant="brand-link"
            size="sm"
            onClick={() =>
              router.push({
                pathname: '/compliance-monitor',
                query: { view: 'calendar' },
              })
            }
          >
            View all
          </Button>
        )}
      </div>

      {section.visible && (
        <>
          {isLoading && (
            <div className="flex min-h-[220px] items-center justify-center p-3xl">
              <Loading />
            </div>
          )}

          {!isLoading && hasFilings && (
            <CalendarAgendaView
              eventsByDate={eventsByDate}
              currentMonth={today.toISO8601String()}
              onSelectEvent={handleSelectEvent}
              mode="horizontal"
              calendarNotGenerated={calendarNotGenerated}
              viewStart={today.toISO8601String()}
              viewEnd={windowEnd.toISO8601String()}
              className="flex-none rounded-none border-0 bg-transparent p-0 [&_.overflow-auto]:!p-lg"
            />
          )}

          {!isLoading && !hasFilings && (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-xs px-xl text-center">
              <span className="txt-small-bold text-text-primary">
                {calendarNotGenerated
                  ? 'Compliance calendar not set up yet'
                  : `No filings due in the next ${windowDays} days`}
              </span>
              <span className="txt-small-regular text-text-secondary">
                {calendarNotGenerated
                  ? 'Set up your compliance calendar to see upcoming filings here.'
                  : 'Filings will appear here as their due dates approach.'}
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
};
